import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { ApiHttpClient } from 'app/http/clients/api';
import { Observable } from 'rxjs';
import { Document } from 'app/models/dto/document.model';
import { environment } from 'environments/environment';
import { map } from 'rxjs/operators';
import { DocumentCategory } from 'app/models/dto/document-category.model';
import { Store } from '@ngxs/store';
import { PersonalCreditReport } from 'app/models/dto/credit-reports/personal-credit-report.model';

@Injectable({
  providedIn: 'root'
})
export class DocumentsService {

    constructor(
        private http: ApiHttpClient,
        private store: Store,
    ) {}

    getBorrowerDocuments(borrowerId: number): Observable<Document[]> {
        const query = {
            criteria: {
                'documents.borrowerId': {'==': borrowerId}
            },
            sort: {'documents.created': 'DESC'}
        };
        const queryString = encodeURIComponent(JSON.stringify(query));
        return this.http.get<Document[]>(`${environment.API_URL}/document/borrower/${borrowerId}/?query=${queryString}`);
    }

    getViewableMimetypes(): Observable<any> {
        return this.http.get(`${environment.API_URL}/document/viewable-mimetypes`);
    }

    // gets all documents categories
    getDocumentCategories(): Observable<DocumentCategory[]> {
        return this.http.get<DocumentCategory[]>(`${environment.API_URL}/document-category`);
    }

    // upload a new document
    upload(file: File, borrowerId: number): Observable<Document> {
        // remove all backslahes in a file name
        const fileNameSanitized = file && file.name && file.name.replace(/\\/g, '');
        // create a new instance of a file to pass with the name sanitized ( stripped slahes) since file is immutable
        const newFile = new File([file], fileNameSanitized);
        const formData = new FormData();
        formData.append('file',  fileNameSanitized ? newFile : file);
        formData.append('borrowerId', `${borrowerId}`);
        if (file['category']) {
            formData.append('category', file['category']);
        }
        if (file['contactId']) {
          formData.append('contactId', file['contactId']);
        }
        if (file['years']) {
          formData.append('years', JSON.stringify(file['years']));
        }
        return this.http.post<Document>(`${environment.API_URL}/document`, formData).pipe(
            map((document: any) => {
                document.lenderUser = document.lender_user;
                return document;
            })
        );
    }

    advancedUpload(file: File, formData: FormData): Observable<Document> {
        const fileNameSanitized = file && file.name && file.name.replace(/\\/g, '');
        // create a new instance of a file to pass with the name sanitized ( stripped slahes) since file is immutable
        const newFile = new File([file], fileNameSanitized);
        formData.append('file', fileNameSanitized ? newFile : file);
        formData.append('uploadSource', 'lenderPortal');

        return this.http.post<Document>(`${environment.API_URL}/document`, formData).pipe(
            map((document: Document) => {
                document.lenderUser = document.lender_user ?? null;
                return document;
            })
        );
    }
    // delete a single document by documentId
    delete(documentId: number): Observable<number> {
        return this.http.delete<string>(`${environment.API_URL}/document/${documentId}`).pipe(map(docId => parseInt(docId)));
    }

    // update a single document by documentId
    update(document: any): Observable<Document> {
        return this.http.put<Document>(`${environment.API_URL}/document/${document.id}`, document);
    }

    setDocumentContact(documentId: number, contactId: number): Observable<Document> {
      return this.http.post<Document>(`${environment.API_URL}/document/${documentId}/set-contact`, {
        contactId,
      });
    }

    // update the verification of a single document by documentId
    updateDrsVerify(document: Document): Observable<Document> {
        const shouldAddVerification = !document.verificationCreated;
        return this.http.put<Document>(`${environment.API_URL}/document/${document.id}/drsVerify`, { verify: shouldAddVerification });
    }

    getApplicationPreview(borrowerId: number) {
        return this.http.get(`borrower/${borrowerId}/borrower-application`);
    }

    saveApplication(borrowerId: number) {
        return this.http.post(`borrower/${borrowerId}/borrower-application`, {});
    }

    repullCredit(borrowerId: number) {
        return this.http.get<PersonalCreditReport>(`${environment.API_URL}/experian/credit-score/borrower/${borrowerId}`);
    }

    runExperianCommercial(borrowerId: number) {
        return this.http.post(`experian-commercial/premier-report/${borrowerId}`, {});
    }
}
